/**
 * Third-party script loaders.
 *
 * These used to be inline <script> tags in the Django SPA shell
 * (webapp/templates/webapp/new-ui.html), where template conditions could gate them
 * on company and deployment state. The shell is now also published as a static file
 * to Cloudflare Pages (ui/public/index.html), which has no server to evaluate those
 * conditions — so anything conditional loads from here instead, once app data has
 * arrived. A tag left in either shell AND a loader here means it loads twice.
 */

import { initPosthog } from './posthog'

const GTM_ID = 'GTM-WC2KS2N'
const GA_MEASUREMENT_ID = 'UA-53083811-3'
const PENDO_API_KEY = 'e23ec49d-0131-4fdd-7a2e-22b1cac417f2'
const PENDO_AGENT_URL = `https://content.pendo.eu.members.ironscales.com/agent/static/${PENDO_API_KEY}/pendo.js`
const BABLIC_MOXIE_URL = 'https://edge.bablic.com/moxie.min.js'
const BABLIC_MOXIE_OLJS = 'PC36A-36E7-16BC-C819'
const STATUSPAGE_EMBED_URL = 'https://4hyb9xl50gtg.statuspage.io/embed/script.js'

// Same guard initSentry uses: GIT_VERSION is '' under jest, so no unit test appends
// a third-party script or lets a request escape the run.
const isRealBuild = () => Boolean(GIT_VERSION)

const loaded = new Set()

const appendScript = (props) => {
  const script = document.createElement('script')
  Object.assign(script, props)
  document.head.appendChild(script)
  return script
}

// initializeServices can run twice (componentDidMount with app data already in the
// store, then an isDataLoaded transition), so loading has to be idempotent rather
// than rely on being called once — GTM loaded twice double-counts every event.
const loadOnce = (key, load) => {
  if (!isRealBuild() || loaded.has(key)) return

  loaded.add(key)
  load()
}

const loadGoogleTagManager = () =>
  loadOnce('gtm', () => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })
    appendScript({
      async: true,
      src: `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`,
    })
  })

const loadGoogleAnalytics = () =>
  loadOnce('ga', () => {
    appendScript({
      async: true,
      src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
    })
    window.dataLayer = window.dataLayer || []
    // gtag forwards `arguments` verbatim, which an arrow function cannot do.
    // eslint-disable-next-line func-names
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID)
  })

const loadBablic = () =>
  loadOnce('bablic', () => {
    const moxie = appendScript({
      src: BABLIC_MOXIE_URL,
      referrerPolicy: 'no-referrer-when-downgrade',
    })
    moxie.dataset.oljs = BABLIC_MOXIE_OLJS
  })

// The official Pendo snippet, loaded by the app itself instead of via a GTM tag.
// Pendo used to arrive through GTM, which App.initializeServices only *starts*
// loading — window.pendo was almost never there yet when the app checked for it,
// so pendo.initialize was silently skipped. The snippet's stub queues every call
// made before the agent script arrives, so initialize can run immediately and
// there is no race to lose. (The GTM container's Pendo tag should be retired;
// until then the agent's own guard prevents a double load from double-counting.)
const loadPendo = () =>
  loadOnce('pendo', () => {
    /* eslint-disable no-underscore-dangle -- _q is the agent's own queue name */
    window.pendo = window.pendo || {}
    const pendo = window.pendo
    pendo._q = pendo._q || []
    const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']
    methods.forEach((method) => {
      pendo[method] =
        pendo[method] ||
        ((...args) => pendo._q[method === 'initialize' ? 'unshift' : 'push']([method, ...args]))
    })
    /* eslint-enable no-underscore-dangle */
    appendScript({
      async: true,
      src: PENDO_AGENT_URL,
      // A failed agent fetch (blocked CDN, proxy, outage) must be loud: the stub
      // keeps queueing silently, and nothing else would ever report the loss.
      onerror: () => {
        // eslint-disable-next-line no-console
        console.error(`Pendo agent failed to load from ${PENDO_AGENT_URL}`)
      },
    })
  })

const loadPostHog = (context) => loadOnce('posthog', () => initPosthog(context))

const loadStatusPage = () =>
  loadOnce('statuspage', () => {
    appendScript({
      defer: true,
      src: STATUSPAGE_EMBED_URL,
      crossOrigin: 'anonymous',
    })
  })

/**
 * @param {object}  options
 * @param {boolean} options.isOnPremise  environment === 'ONPREMISE'
 * @param {boolean} options.isSaas       environment === 'PROD' (the `is_saas` flag)
 * @param {boolean} options.allowBablic  ENABLE_BABLIC_FF for the company
 * @param {string}  [options.deployment] settings.DEPLOYMENT (PROD, STAGING, …)
 * @param {string}  [options.region]     settings.REGION (US, EU, CA, …)
 * @returns {Promise<void>} resolves when the app may paint
 */
export const initThirdPartyScripts = ({ isOnPremise, isSaas, allowBablic, deployment, region }) => {
  // On-premise installs must not call out to third parties. GA and Bablic were
  // already gated this way in the Django shell; GTM was not, and is now.
  if (isOnPremise) return Promise.resolve()

  loadGoogleTagManager()
  loadGoogleAnalytics()
  loadPendo()
  loadPostHog({ deployment, region })

  if (allowBablic) loadBablic()
  if (isSaas) loadStatusPage()

  return Promise.resolve()
}
